import React, { useState } from 'react'

import { ServiceProvider, UserCompanyDto, UserProfileDto } from '../../services/api.types'
import { Box, List, ListItem, ListItemIcon } from '@material-ui/core'
import { t } from 'ttag'

import YumButton from '../../components/YumButton/YumButton'

import Typography from '@material-ui/core/Typography'
import makeStyles from '@material-ui/core/styles/makeStyles'
import ListItemText from '@material-ui/core/ListItemText'

import { ReactComponent as CheckedCircleIcon } from '../../assets/v2/icons/checked-circle-blue.svg'
import { ReactComponent as EmptyCircleIcon } from '../../assets/v2/icons/empty-circle-blue.svg'
import { ReactComponent as GoogleIcon } from '../../assets/v2/icons/icon-google.svg'
import { ReactComponent as DropdownIcon } from '../../assets/v3/icons/notifications/dropdown.svg'

import NewAppleHealthIcon from '../../assets/v3/icons/integration/apple-health.svg'
import NewHealthConnectIcon from '../../assets/v3/icons/integration/health-connect.svg'
import FitbitIcon from '../RegisterUser/images/fitbit.svg'
import GarminIcon from '../RegisterUser/images/garmin.svg'
import GoogleFitIcon from '../RegisterUser/images/google-fit.svg'
import GoogleHealthIcon from '../RegisterUser/images/google-health.svg'
import PolarIcon from '../RegisterUser/images/polar.svg'
import SuuntoIcon from '../RegisterUser/images/suunto.svg'

import { isAndroid, isIOS } from '../../common/helpers/isNative'
import { ROUTES } from '../../common/Routes/routePaths'
import YumCancelButton from '../../components/YumCancelButton/YumCancelButton'
import { darkBlue, primaryBlue } from '../../styles/material-ui-theme-v2'
import { tokens } from '../../styles/tokens'
import { Card } from '../../components/Card/Card'
import ErrorMessage from '../../components/ErrorMessage/ErrorMessage'
import NotificationsWrapper from '../../components/Notification/NotificationsWrapper'
import { DesktopSidebar } from '../../components/DesktopSidebar/DesktopSidebar'
import { hasFeatureFlag } from '../../common/helpers/featureFlags'

export interface IIntegrationsProps {
  userDto: UserProfileDto
  lastUpdate: number | null
  userCompanies: UserCompanyDto[]
  isDuringRegistration?: boolean
}

export interface IIntegrationsDispatchProps {
  registerAppleHealth(): void
  registerWithoutIntegration(): void
  registerHealthConnect(): void
}

const useStyles = makeStyles((theme) => ({
  title: { marginTop: '30px', marginBottom: '20px' },
  // The "Works with" disclosure sits OUTSIDE the radio (a11y: a radio must not
  // contain a focusable button). Pull it up under the card and align it with the
  // card's gutters so it still reads as part of the row visually.
  disclosure: {
    width: '100%',
    marginTop: '-8px',
    marginBottom: '16px',
    padding: '0 10px',
  },
  iconsContainer: {
    padding: '10px 0 0',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '10px',
    alignItems: 'center',
    fontSize: '0.8125rem',
  },
  googleFitButton: {
    width: 'auto',
    whiteSpace: 'nowrap',
    margin: '0 auto',
    lineHeight: '18px',
    display: 'inline-flex',
    textTransform: 'unset',

    fontFamily: 'Roboto, arial, sans-serif',
    fontSize: '1rem',
    fontWeight: 500,
    letterSpacing: '.21px',

    backgroundColor: '#4285f4',
    padding: 0,
    border: '1px solid #4285f4',
    overflow: 'hidden',

    borderRadius: '2px',
    transition: 'none',
  },
  listItem: { marginBottom: '16px', flexWrap: 'wrap' },
  listItemDisabled: {
    borderColor: 'transparent !important',
    cursor: 'default',
    backgroundColor: 'rgba(245, 245, 249, 0.5) !important',
  },
  // Disabled row content keeps full opacity (only the background tint is at 0.5);
  // the radio circle goes dark gray instead of brand blue. #616161 holds >=3:1
  // on the muted #F5F5F9 row (WCAG 2.2 SC 1.4.11), which tokens.text.disabled
  // (#8A8EA5, 2.95:1) just misses.
  disabledCircle: {
    '& path': { stroke: '#616161' },
  },
  actionButtons: { display: 'flex', /*marginTop: '40px',*/ gap: '24px', justifyContent: 'center' },
  link: {
    // Sits on the muted #F5F5F9 integration row; primaryBlue is only 4.27:1 there.
    // blueHover reaches 5.1:1 on muted / 5.9:1 on white (WCAG 2.2 SC 1.4.3).
    color: tokens.brand.blueHover,
    cursor: 'pointer',
    '@media (hover:hover)': {
      '&:hover': { textDecoration: 'underline' },
    },
    '&:active': { textDecoration: 'underline' },
  },
  dropdown: { display: 'flex', gap: '4px', alignItems: 'center' },
  dropdownIcon: {
    color: primaryBlue,
    transition: 'transform 150ms',
  },
  divider: { height: '1px', background: darkBlue, width: '100%', opacity: 0.5 },
  worksWithGrid: { display: 'grid', gridTemplateColumns: 'auto auto', gap: '10px', width: '100%' },
  primaryTypography: {
    fontWeight: 'bold',
    fontSize: '0.9375rem',
  },
  secondaryTypography: {
    fontSize: '0.8125rem',
    color: darkBlue,
  },
}))

export default function Integrations(props: IIntegrationsProps & IIntegrationsDispatchProps) {
  const classes = useStyles()
  const {
    userDto,
    lastUpdate,
    isDuringRegistration,
    userCompanies,
    registerAppleHealth,
    registerWithoutIntegration,
    registerHealthConnect,
  } = props

  React.useEffect(() => {
    if (!isDuringRegistration) {
      setTimeout(() => {
        document.body.classList.add('show-back-btn')
      }, 0)
    }
    return function cleanup() {
      if (!isDuringRegistration) {
        document.body.classList.remove('show-back-btn')
      }
    }
  }, [])

  const [showOldGoogleFit, setShowOldGoogleFit] = useState(false)
  const [showGoogleHealth, setShowGoogleHealth] = useState(false)
  const [hideHealthConnect, setHideHealthConnect] = useState(true)
  const [hideFitbit, setHideFitbit] = useState(false)

  React.useEffect(() => {
    const { serviceProvider: currentServiceProvider } = userDto
    // The hide flags apply to users currently on the provider too — migration semantics: they
    // should pick a new integration, not re-select the deprecated one.
    setShowOldGoogleFit(
      (currentServiceProvider === ServiceProvider.GOOGLE_FIT || hasFeatureFlag(userCompanies, 'showOldGoogleFit')) &&
        !hasFeatureFlag(userCompanies, 'hideGoogleFit'),
    )
    setShowGoogleHealth(
      currentServiceProvider === ServiceProvider.GOOGLE_HEALTH || hasFeatureFlag(userCompanies, 'googleHealthBeta'),
    )
    setHideHealthConnect(
      currentServiceProvider !== ServiceProvider.HEALTH_CONNECT && hasFeatureFlag(userCompanies, 'hideHealthConnect'),
    )
    setHideFitbit(hasFeatureFlag(userCompanies, 'hideFitbit'))
  }, [userCompanies])

  const fitbitDeprecated = hasFeatureFlag(userCompanies, 'fitbitDeprecationNotice')
  const googleFitDeprecated = hasFeatureFlag(userCompanies, 'googleFitDeprecationNotice')
  const deprecationText = t`Deprecated by Google — this integration will stop working soon. Please choose another one, for example Google Health.`

  const [submitInProgress, setSubmitInProgress] = useState(false)
  const [integration, setIntegration] = useState(undefined as string | null | undefined)

  const [isAppleHealthOpen, setIsAppleHealthOpen] = useState(false)
  const [isHealthConnectOpen, setIsHealthConnectOpen] = useState(false)
  // Shown when the user submits without choosing an option. `null` (No
  // integration) is a valid choice; only `undefined` means nothing selected.
  const [showSelectionError, setShowSelectionError] = useState(false)

  React.useEffect(() => {
    setSubmitInProgress(false)
  }, [lastUpdate])

  // Clear the validation error as soon as the user picks any option.
  React.useEffect(() => {
    if (integration !== undefined) {
      setShowSelectionError(false)
    }
  }, [integration])

  const onClickIntegration = () => {
    if (integration === undefined) {
      // Nothing selected — surface an error the screen reader announces (the
      // ErrorMessage has role="alert"), instead of silently doing nothing.
      setShowSelectionError(true)
      return
    }
    const serviceProvider = integration
    if (serviceProvider === ServiceProvider.APPLE_HEALTH) {
      setSubmitInProgress(true)
      registerAppleHealth()
    } else if (serviceProvider === ServiceProvider.HEALTH_CONNECT) {
      setSubmitInProgress(true)
      registerHealthConnect()
    } else if (serviceProvider === null) {
      setSubmitInProgress(true)
      registerWithoutIntegration()
    }
  }

  const doIntegration = () => {
    if (
      integration === ServiceProvider.APPLE_HEALTH ||
      integration === ServiceProvider.HEALTH_CONNECT ||
      integration === null
    ) {
      return ''
    }

    const { dateOfBirth } = userDto
    const scope = !dateOfBirth ? 'LEAN' : 'FULL'
    let extraParams = ''
    if (integration === ServiceProvider.GOOGLE_HEALTH) {
      // Request GPS route and sleep scopes only when the company features are on.
      if (hasFeatureFlag(userCompanies, 'locationTracking')) extraParams += '&d=1'
      if (hasFeatureFlag(userCompanies, 'sleepSync')) extraParams += '&s=1'
    }
    return integration ? `${ROUTES.INTEGRATION_PROXY.to}?type=${integration}&scope=${scope}${extraParams}` : ''
  }

  const handleAppleHealthToggle = () => {
    setIsAppleHealthOpen(!isAppleHealthOpen)
  }

  const handleHealthConnectToggle = () => {
    setIsHealthConnectOpen(!isHealthConnectOpen)
  }

  const appleHealthIcons = (
    <div className={classes.iconsContainer}>
      {/*<div className={classes.divider}></div>*/}
      <div>
        {/* Disclosure toggle. Lives outside the radio (sibling of the row card),
            so it is its own focus stop and does not double up the radio. */}
        <button
          type="button"
          className={classes.dropdown}
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
          aria-expanded={isAppleHealthOpen}
          onClick={handleAppleHealthToggle}
        >
          <DropdownIcon
            aria-hidden="true"
            className={classes.dropdownIcon}
            style={{
              transform: isAppleHealthOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
          <Box className={classes.link}>{t`Works with`}</Box>
        </button>
      </div>
      {isAppleHealthOpen && (
        <div className={classes.worksWithGrid}>
          <div>
            Apple iPhone
            <br />
            Apple Watch
            <br />
            adidas Running
            <br />
            Coros
            <br />
            HUAWEI Health
            <br />
            Mi Fitness
          </div>
          <div>
            Oura
            <br />
            Strava
            <br />
            Zepp Life (MiFit)
            <br />
            Zepp (Amazfit)
            <br />
            Withings
            <br />
            Whoop
          </div>
        </div>
      )}
    </div>
  )

  const healthConnectIcons = (
    <div className={classes.iconsContainer}>
      {/*<div className={classes.divider}></div>*/}
      <div>
        <button
          type="button"
          className={classes.dropdown}
          style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
          aria-expanded={isHealthConnectOpen}
          onClick={handleHealthConnectToggle}
        >
          <DropdownIcon
            aria-hidden="true"
            className={classes.dropdownIcon}
            style={{
              transform: isHealthConnectOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            }}
          />
          <Box className={classes.link}>{t`Works with`}</Box>
        </button>
      </div>
      {isHealthConnectOpen && (
        <div className={classes.worksWithGrid}>
          <div>
            Android
            <br />
            adidas Running
            <br />
            Coros
            <br />
            Fitbit
            <br />
            Google Fit
            <br />
            Oura
          </div>
          <div>
            Samsung Health
            <br />
            Wahoo
            <br />
            WHOOP
            <br />
            Withings
            <br />
            Zepp (Amazfit)
            <br />
            ...
          </div>
        </div>
      )}
    </div>
  )

  const Title = ({ children }: { children: any }) => (
    <Typography variant="h2" className={classes.title}>
      {children}
    </Typography>
  )

  const phoneIntegrations = (
    <>
      {/*<Title>{t`Phone integration`}</Title>*/}
      <List component="div" role="presentation">
        {/* The radio is the top row only (icon + name + circle) so it stays a
            single leaf focus stop. The "Works with" disclosure has its own
            button, so it lives OUTSIDE the radio as a sibling below — otherwise
            screen readers stop on the row twice (WCAG 1.3.1 / 4.1.2).
            The row's visible content is aria-hidden and the whole name is given
            by aria-label: ListItemText renders `secondary` as a block-level <p>,
            which NVDA reads as a second stop ("radio button ... Apple Health",
            "radio button ... iOS device") unless the subtree is hidden. */}
        <ListItem
          button
          disableRipple
          role="radio"
          aria-checked={integration === 'APPLE_HEALTH'}
          aria-disabled={!isIOS}
          aria-label={`Apple Health, ${t`iOS device`}`}
          selected={integration === 'APPLE_HEALTH'}
          onClick={isIOS ? () => setIntegration('APPLE_HEALTH') : () => {}}
          className={isIOS ? classes.listItem : [classes.listItem, classes.listItemDisabled].join(' ')}
        >
          <ListItemIcon aria-hidden="true">
            <img style={{ width: '2.25rem' }} src={NewAppleHealthIcon} alt={`Apple Health`} />
          </ListItemIcon>
          <ListItemText
            aria-hidden="true"
            primary={'Apple Health'}
            secondary={t`iOS device`}
            primaryTypographyProps={{
              className: classes.primaryTypography,
            }}
            secondaryTypographyProps={{
              className: classes.secondaryTypography,
            }}
          />
          <ListItemIcon aria-hidden="true">
            {integration === 'APPLE_HEALTH' ? (
              <CheckedCircleIcon />
            ) : (
              <EmptyCircleIcon className={isIOS ? undefined : classes.disabledCircle} />
            )}
          </ListItemIcon>
        </ListItem>
        <div className={classes.disclosure}>
          <p className="integration-disabled-text">
            {!isIOS ? t`To use Apple Health, use YuMuuv iOS app` : t`Make sure your data is synced to Apple Health`}
          </p>
          {appleHealthIcons}
        </div>
        {!hideHealthConnect && (
          <>
            <ListItem
              button
              disableRipple
              role="radio"
              aria-checked={integration === 'HEALTH_CONNECT'}
              aria-disabled={!isAndroid}
              aria-label={`Health Connect, ${t`Android device`}`}
              selected={integration === 'HEALTH_CONNECT'}
              onClick={isAndroid ? () => setIntegration('HEALTH_CONNECT') : () => {}}
              className={isAndroid ? classes.listItem : [classes.listItem, classes.listItemDisabled].join(' ')}
            >
              <ListItemIcon aria-hidden="true">
                <img style={{ width: '2.25rem' }} src={NewHealthConnectIcon} alt={`Health Connect`} />
              </ListItemIcon>
              <ListItemText
                aria-hidden="true"
                primary={'Health Connect'}
                secondary={t`Android device`}
                primaryTypographyProps={{
                  className: classes.primaryTypography,
                }}
                secondaryTypographyProps={{
                  className: classes.secondaryTypography,
                }}
              />
              <ListItemIcon aria-hidden="true">
                {integration === 'HEALTH_CONNECT' ? (
                  <CheckedCircleIcon />
                ) : (
                  <EmptyCircleIcon className={isAndroid ? undefined : classes.disabledCircle} />
                )}
              </ListItemIcon>
            </ListItem>
            <div className={classes.disclosure}>
              <p className="integration-disabled-text">
                {!isAndroid
                  ? t`To use Health Connect, use YuMuuv Android app`
                  : t`Make sure your data is synced to Health Connect`}
              </p>
              {healthConnectIcons}
            </div>
          </>
        )}
      </List>
    </>
  )

  const mainForm = (
    <div
      role="radiogroup"
      aria-label={t`Choose integration`}
      aria-invalid={showSelectionError}
      aria-describedby={showSelectionError ? 'integration-selection-error' : undefined}
    >
      {phoneIntegrations}

      {/*<Title>{t`Cloud integration`}</Title>*/}
      <List component="div" role="presentation">
        {showGoogleHealth && (
          <ListItem
            button
            disableRipple
            onClick={() => setIntegration('GOOGLE_HEALTH')}
            role="radio"
            aria-checked={integration === 'GOOGLE_HEALTH'}
            selected={integration === 'GOOGLE_HEALTH'}
            className={classes.listItem}
          >
            <ListItemIcon>
              <img
                className="google-health"
                style={{ height: '1.5rem', width: 'auto' }}
                src={GoogleHealthIcon}
                alt={`Google Health`}
              />
            </ListItemIcon>
            <ListItemText
              primary={'Google Health'}
              primaryTypographyProps={{
                className: classes.primaryTypography,
              }}
            />
            <ListItemIcon>{integration === 'GOOGLE_HEALTH' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
          </ListItem>
        )}

        {!hideFitbit && (
          <>
            <ListItem
              button
              disableRipple
              onClick={() => setIntegration('FITBIT')}
              role="radio"
              aria-checked={integration === 'FITBIT'}
              selected={integration === 'FITBIT'}
              className={classes.listItem}
            >
              <ListItemIcon>
                <img
                  className="fitbit"
                  style={{ height: '1.1875rem', width: 'auto' }}
                  src={FitbitIcon}
                  alt={`Fitbit`}
                />
              </ListItemIcon>
              <ListItemText primary={'' /*`Fitbit`*/} />
              <ListItemIcon>{integration === 'FITBIT' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
            </ListItem>
            {fitbitDeprecated && (
              <div className={classes.disclosure}>
                <p className="integration-disabled-text">{deprecationText}</p>
              </div>
            )}
          </>
        )}

        <ListItem
          button
          disableRipple
          onClick={() => setIntegration('GARMIN')}
          role="radio"
          aria-checked={integration === 'GARMIN'}
          selected={integration === 'GARMIN'}
          className={classes.listItem}
        >
          <ListItemIcon>
            <img className="garmin" style={{ height: '1.375rem', width: 'auto' }} src={GarminIcon} alt={`Garmin`} />
          </ListItemIcon>
          <ListItemText primary={'' /*`Garmin Connect`*/} />
          <ListItemIcon>{integration === 'GARMIN' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
        </ListItem>

        {showOldGoogleFit && (
          <>
            <ListItem
              button
              disableRipple
              onClick={() => setIntegration('GOOGLE_FIT')}
              role="radio"
              aria-checked={integration === 'GOOGLE_FIT'}
              selected={integration === 'GOOGLE_FIT'}
              className={classes.listItem}
            >
              <ListItemIcon>
                <img
                  className="google-fit"
                  style={{ height: '1.3125rem', width: 'auto' }}
                  src={GoogleFitIcon}
                  alt={`Google Fit`}
                />
              </ListItemIcon>
              <ListItemText primary={'' /*`Google Fit`*/} />
              <ListItemIcon>{integration === 'GOOGLE_FIT' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
            </ListItem>
            {googleFitDeprecated && (
              <div className={classes.disclosure}>
                <p className="integration-disabled-text">{deprecationText}</p>
              </div>
            )}
          </>
        )}

        <ListItem
          button
          disableRipple
          onClick={() => setIntegration('POLAR')}
          role="radio"
          aria-checked={integration === 'POLAR'}
          selected={integration === 'POLAR'}
          className={classes.listItem}
        >
          <ListItemIcon>
            <img className="polar" style={{ height: '0.75rem', width: 'auto' }} src={PolarIcon} alt={`Polar`} />
          </ListItemIcon>
          <ListItemText primary={'' /*`Polar Flow`*/} />
          <ListItemIcon>{integration === 'POLAR' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
        </ListItem>

        <ListItem
          button
          disableRipple
          onClick={() => setIntegration('SUUNTO')}
          role="radio"
          aria-checked={integration === 'SUUNTO'}
          selected={integration === 'SUUNTO'}
          className={classes.listItem}
        >
          <ListItemIcon>
            <img className="suunto" style={{ height: '1.375rem', width: 'auto' }} src={SuuntoIcon} alt={`Suunto`} />
          </ListItemIcon>
          <ListItemText primary={'' /*`Suunto`*/} />
          <ListItemIcon>{integration === 'SUUNTO' ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
        </ListItem>
      </List>

      {/*<Title>{t`Other`}</Title>
      <List>
        <ListItem button disableRipple onClick={handleOther} className={classes.listItem}>
          <ListItemText primary={t`Other`} style={{ marginLeft: '0' }} />
          <ListItemIcon>
            <ArrowForwardIcon />
          </ListItemIcon>
        </ListItem>
      </List>*/}

      {/*<Title>{t`No integration`}</Title>*/}
      <List component="div" role="presentation">
        <ListItem
          button
          disableRipple
          onClick={() => setIntegration(null)}
          role="radio"
          aria-checked={integration === null}
          selected={integration === null}
          className={classes.listItem}
        >
          <ListItemIcon>{t`No integration`}</ListItemIcon>
          <ListItemText primary={'' /*t`No integration`*/} />
          <ListItemIcon>{integration === null ? <CheckedCircleIcon /> : <EmptyCircleIcon />}</ListItemIcon>
        </ListItem>
      </List>
    </div>
  )

  const nextButton =
    integration === 'GOOGLE_FIT' || integration === 'GOOGLE_HEALTH' ? (
      <div style={{ textAlign: 'center' }}>
        <YumButton className={classes.googleFitButton} to={doIntegration()}>
          <div style={{ display: 'flex', flexDirection: 'row' }}>
            <div
              style={{
                backgroundColor: 'white',
                padding: '14px',
                height: '46px',
                lineHeight: '18px',
              }}
            >
              <GoogleIcon />
            </div>
            <span
              style={{
                margin: '0 14px',
                padding: '14px 0',
              }}
            >{t`Sign in with Google`}</span>
          </div>
        </YumButton>
      </div>
    ) : (
      <YumButton
        showLoader={true}
        disabled={submitInProgress}
        onClick={onClickIntegration}
        to={doIntegration()}
      >{t`Next`}</YumButton>
    )

  const actionButtons = (
    <>
      {showSelectionError && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
          <ErrorMessage id="integration-selection-error">{t`Please select an option to continue.`}</ErrorMessage>
        </div>
      )}
      <div className={classes.actionButtons}>{nextButton}</div>
    </>
  )

  const form = (
    <>
      {mainForm}
      {actionButtons}
    </>
  )

  const content = (
    <>
      <Typography
        component="h1"
        variant="subtitle2"
        style={{ marginBottom: '24px' }}
      >{t`Choose integration`}</Typography>
      {form}
    </>
  )

  return isDuringRegistration ? (
    content
  ) : (
    <Card className="onboarding">
      <div className="onboarding__form">
        <NotificationsWrapper />
        {content}
      </div>
      <DesktopSidebar />
    </Card>
  )
}
